import { useEffect, useMemo, useRef, useState } from 'react';

// A dropdown you can type into: closed it looks like a select; clicked, it shows an empty
// search box and the list, which narrows as you type (every word must match the label or
// detail; "262dl" also finds "262-DL-10"). Arrow keys + Enter pick, Escape closes.
export type SearchOption = { value: string; label: string; detail?: string; group?: string };

const squash = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');

export function SearchSelect({ id, value, options, onChange, placeholder = 'Choose…', emptyLabel, required, disabled }: {
  id: string;
  value: string;
  options: SearchOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyLabel?: string; // adds a first entry with value '' (e.g. "– none –")
  required?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const refocus = useRef(false);

  const all = useMemo(() => (emptyLabel === undefined ? options : [{ value: '', label: emptyLabel }, ...options]), [options, emptyLabel]);
  const shown = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return all;
    return all.filter((o) => {
      const hay = `${o.label} ${o.detail ?? ''} ${o.group ?? ''}`.toLowerCase();
      const flat = squash(hay);
      return words.every((w) => hay.includes(w) || flat.includes(squash(w)));
    });
  }, [all, q]);
  const current = all.find((o) => o.value === value);

  const show = () => {
    if (disabled) return;
    setQ('');
    setActive(Math.max(0, all.findIndex((o) => o.value === value)));
    setOpen(true);
  };
  const close = () => { setOpen(false); refocus.current = true; };
  const pick = (o: SearchOption) => { close(); if (o.value !== value) onChange(o.value); };

  useEffect(() => {
    if (!open && refocus.current) { refocus.current = false; button.current?.focus(); }
  }, [open]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (shown[active]) pick(shown[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
    else if (e.key === 'Tab') setOpen(false);
  };

  let lastGroup: string | undefined;
  return (
    <div className={`ss${open ? ' open' : ''}`}>
      {open ? (
        <input id={id} type="text" autoFocus autoComplete="off" spellCheck={false}
          role="combobox" aria-expanded="true" aria-controls={`${id}-list`} aria-autocomplete="list"
          aria-activedescendant={shown[active] ? `${id}-o${active}` : undefined}
          placeholder={current?.value ? current.label : 'Type to search…'} value={q}
          onChange={(e) => { setQ(e.target.value); setActive(0); }} onKeyDown={onKey} onBlur={() => setOpen(false)} />
      ) : (
        <button ref={button} id={id} type="button" className="ss-btn" disabled={disabled} aria-haspopup="listbox"
          onClick={show} onKeyDown={(e) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); show(); } }}>
          {current ? <span className={current.value ? '' : 'muted'}>{current.label}{current.detail && <span className="muted"> · {current.detail}</span>}</span>
            : <span className="muted">{placeholder}</span>}
        </button>
      )}
      {/* Lets the browser's own "please fill in" check work on required pickers. */}
      {required && <input className="ss-check" tabIndex={-1} aria-hidden="true" required value={value} onChange={() => {}} onFocus={show} />}
      {open && (
        <ul ref={list} id={`${id}-list`} role="listbox" className="ss-list">
          {shown.length === 0 && <li className="ss-none muted">No matches</li>}
          {shown.map((o, i) => {
            const head = o.group && o.group !== lastGroup ? <li key={`g-${o.group}`} className="ss-group" role="presentation">{o.group}</li> : null;
            lastGroup = o.group;
            return [head,
              <li key={o.value} id={`${id}-o${i}`} data-i={i} role="option" aria-selected={o.value === value}
                className={`${i === active ? 'active' : ''}${o.value === value ? ' chosen' : ''}`}
                onMouseDown={(e) => e.preventDefault()} onMouseMove={() => setActive(i)}
                onClick={(e) => { e.preventDefault(); pick(o); }}>
                <span className={o.value ? '' : 'muted'}>{o.label}</span>{o.detail && <span className="muted small"> · {o.detail}</span>}
              </li>];
          })}
        </ul>
      )}
    </div>
  );
}
